import React, { useMemo, forwardRef, useImperativeHandle, useCallback } from 'react'
import Switch from "Common/Custom/Switch";
import FormSelect from "Common/Custom/FormSelect";
import FormPhoneInput from "Common/Custom/FormPhoneInput";
import FormDatePicker from "Common/Custom/ReactFlatpickr";
import FormTextarea from "Common/Custom/FormTextarea";
import CountryName from "Common/json/CountryName.json";
import FormInput from "Common/Custom/FormInput";
import FormInputFile from "Common/Custom/FormInputFile";
import fields from "./json/CustomerFeilds.json";
import { validateForm } from 'utils/validation';
import useFormChange from 'Hooks/useFormChange';
import { bookingTimeCustomerRules } from 'validation/ValidationRules';
import profileImage from "assets/images/avatar.png";


const CreateNewCustomerForm = forwardRef(({ newCustomer }, ref) => {
    const allFields = useMemo(
        () => [...fields.user_details, ...fields.contact_details, ...fields.identity_details, ...fields.customer_image],
        []
    );

    const initialValues = useMemo(() => {
        return allFields.reduce((acc, field) => {
            acc[field.name] = "";
            return acc;
        }, {});
    }, [allFields]);

    const { values, errors, handleChange, handleFileChange, setErrors } =
        useFormChange(initialValues, bookingTimeCustomerRules);

    const handleSubmit = useCallback((e, formValues) => {
        if (e && e.preventDefault) e.preventDefault();

        const err = validateForm(formValues, bookingTimeCustomerRules);
        setErrors(err);

        if (Object.keys(err).length === 0) {
            const submitData = { ...formValues };
            delete submitData.profile_imagePreview;
            delete submitData.img_backPreview;
            delete submitData.img_frontPreview;
            newCustomer(submitData);
        }
    }, [newCustomer, setErrors]);


    const { user_details, contact_details, identity_details, customer_image } = fields;

    const renderField = (field) => {
        switch (field.type) {
            case "file":
                const previewKey = `${field.name}Preview`;
                const imagePreview = values[previewKey] || (field.name === "profile_image" ? profileImage : "");
                
                return (
                    <FormInputFile
                        key={field.name}
                        field={field}
                        handleChange={handleFileChange}
                        error={errors[field.name]}
                        imagePreview={imagePreview}
                    />
                );
            case "switch":
                return (
                    <Switch
                        key={field.name}
                        values={values[field.name]}
                        handleChange={handleChange}
                        field={field}
                        ValueOne="1"
                        ValueTwo="0"
                        LabelOne="Active"
                        LabelTwo="Inactive"
                    />
                );
            case "select":
                const countryOptions = CountryName.map((country) => ({
                    value: country.name,
                    label: country.name,
                }));
                return (
                    <FormSelect
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        options={(field.name === "nationality" || field.name === "country") ? countryOptions : field.options}
                        error={errors[field.name]}
                    />
                );
            case "tel":
                return (
                    <FormPhoneInput
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        country_code={values['country_dial_code']}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
            case 'date':
                return (
                    <FormDatePicker
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        error={errors[field.name]}
                        dateFormat="Y-m-d"
                    />
                );
            case 'textarea':
                return (
                    <FormTextarea
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
            case 'checkbox':
                return (
                    <div className={field.fieldSize || "col-12 mt-3"}>
                        <div className={`form-check ${field.inline ? "form-check-inline" : ""}`}>
                            <input
                                className={`form-check-input`}
                                type="checkbox"
                                id={field.name + "checkBox"}
                                name={field.name}
                                value={field.value}
                                disabled={field.disabled}
                                onChange={handleChange}
                            />

                            <label className="form-check-label" htmlFor={field.name + "checkBox"}>
                                {field.label}
                            </label>
                        </div>
                    </div>
                );
            default:
                return (
                    <FormInput
                        key={field.name}
                        field={field}
                        value={values[field.name]}
                        handleChange={handleChange}
                        error={errors[field.name]}
                    />
                );
        }
    };

    const testing = (e, values) => {
        handleSubmit(e, values)
    }

    useImperativeHandle(ref, () => ({
        submitForm: () => {
            const fakeEvent = { preventDefault: () => { } };
            handleSubmit(fakeEvent, values);
        }
    }), [values, handleSubmit]);

    return (
        <form onSubmit={(e) => testing(e, values)} >
            <div className="row g-3" >
                {/* User Details */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-primary bg-opacity-10">
                            <h6 className="card-title mb-0 text-primary">User Details</h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {user_details.map((field, idx) => (
                                    <React.Fragment key={`user_details-${field.name}-${idx}`}>
                                        {renderField(field)}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Details */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-success bg-opacity-10">
                            <h6 className="card-title mb-0 text-success">Contact Details</h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {contact_details.map((field, idx) => (
                                    <React.Fragment key={`contact_details-${field.name}-${idx}`}>
                                        {renderField(field)}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Identity Details */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-warning bg-opacity-10">
                            <h6 className="card-title mb-0 text-warning">Identity Details</h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {identity_details.map((field, idx) => (
                                    <React.Fragment key={`identity_details-${field.name}-${idx}`}>
                                        {renderField(field)}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Image */}
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-info bg-opacity-10">
                            <h6 className="card-title mb-0 text-info">Customer Image</h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {customer_image.map((field, idx) => (
                                    <React.Fragment key={`customer_image-${field.name}-${idx}`}>
                                        {renderField(field)}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    )
})

export default CreateNewCustomerForm